import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Input,
  Avatar,
  Typography,
  Flex,
  Dropdown,
  Menu,
  Card,
  Modal,
  message,
  Select,
} from "antd";
import {
  SearchOutlined,
  MoreOutlined,
  PlusOutlined,
  UserAddOutlined,
  FileTextOutlined,
  TrophyOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  StopOutlined,
  CheckCircleOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { UserManagementWrapper, PageHeader, MetricsContainer, TabsContainer } from "../UserManagement/UserManagement.style";
import MaskGroup from "../../../assets/Mask-Group.svg";
import axios from "axios";
import { API_CONST } from "../../../const";
const { Text } = Typography;

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return `₹${Number(amount).toLocaleString('en-IN')}`;
};

const TalentProfiles = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Active");
  const [searchText, setSearchText] = useState("");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [locationFilter, setLocationFilter] = useState(null);
  const [roleFilter, setRoleFilter] = useState(null);
  const [experienceFilter, setExperienceFilter] = useState(null);
  const [organizationFilter, setOrganizationFilter] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);

  const locationOptions = [...new Set(usersData.map(u => u.location).filter(Boolean))];
  const roleOptions = [...new Set(usersData.map(u => u.role).filter(Boolean))];
  const experienceOptions = [...new Set(usersData.map(u => u.experience).filter(Boolean))];
  const organizationOptions = [...new Set(usersData.map(u => u.organization).filter(Boolean))];

  const filteredData = usersData.filter((user) => {
    const tabMatch = activeTab === "Active"
      ? (user.status === "Active" || user.status === "Hired")
      : user.status === activeTab;
    if (!tabMatch) return false;
    if (locationFilter && user.location !== locationFilter) return false;
    if (roleFilter && user.role !== roleFilter) return false;
    if (experienceFilter && user.experience !== experienceFilter) return false;
    if (organizationFilter && user.organization !== organizationFilter) return false;
    if (searchText && !user.name.toLowerCase().includes(searchText.toLowerCase())) return false;
    return true;
  });

  const parseRateString = (rateStr) => {
    if (typeof rateStr === 'number') return rateStr;
    if (!rateStr) return 0;
    return parseInt(rateStr.replace(/[₹,\s]/g, ''), 10) || 0;
  };

  const transformApiData = (apiData) => {
    return apiData.map(item => {
      const rate = item.partnerRate || item.monthlyRate || parseRateString(item.rate) || 0;
      const expYears = item.experienceYears || 0;
      const expMonths = item.experienceMonths || 0;
      return {
        ...item,
        key: item.id,
        id: item.id,
        organization: item.partnerOrganization || item.organization,
        contact: item.phone || item.contact,
        partnerRate: rate,
        clientRate: item.clientRate || rate,
        settledCost: item.settledCost || rate,
        marketRate: item.marketRate || Math.round(rate * 1.1),
        experience: `${expYears}${expMonths > 0 ? `.${expMonths}` : ''} Years`,
        createdOn: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '-',
      };
    });
  };

  const fetchTalentProfiles = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(API_CONST.GET_TALENT_PROFILES, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const transformedData = transformApiData(response.data.Response || []);
      setUsersData(transformedData);
    } catch (error) {
      console.log("Error fetching talent profiles:", error.message);
      message.error("Failed to load talent profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTalentProfiles();
  }, []);

  const activeCount = usersData.filter((user) => user.status === "Active" || user.status === "Hired").length;
  const inactiveCount = usersData.filter((user) => user.status === "Inactive").length;
  const totalCount = usersData.length;

  const handleViewDocuments = (record) => {
    navigate(`/admin/talent-profiles/documents/${record.id || record.key}`, { state: { from: 'list' } });
  };

  const handleEditTalent = (record) => {
    navigate(`/admin/talent-profiles/add-new-profile?edit=${record.id || record.key}`);
  };

  const updateTalentStatus = async (id, newStatus) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.patch(`${API_CONST.UPDATE_TALENT_STATUS}/${id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchTalentProfiles();
      return true;
    } catch (error) {
      console.error("Error updating status:", error);
      message.error("Failed to update status");
      return false;
    }
  };

  const handleMarkInactive = (record) => {
    const id = record.id || record.key;
    Modal.confirm({
      title: "Mark as Inactive",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to mark this talent as inactive?</p>
          <div style={{ marginTop: 12, padding: 12, background: "#f8f9fd", borderRadius: 6 }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.organization}</p>
          </div>
        </div>
      ),
      okText: "Mark Inactive",
      okButtonProps: { style: { background: "#faad14", borderColor: "#faad14" } },
      cancelText: "Cancel",
      onOk: async () => {
        const success = await updateTalentStatus(id, "Inactive");
        if (success) {
          message.success(`${record.name} has been marked as inactive`);
        }
      },
    });
  };

  const handleDeleteTalent = (record) => {
    const id = record.id || record.key;
    Modal.confirm({
      title: "Delete Talent Profile",
      icon: <ExclamationCircleOutlined style={{ color: "#ff4d4f" }} />,
      content: (
        <div>
          <p>Are you sure you want to delete this talent profile?</p>
          <p style={{ color: "#ff4d4f", fontSize: 13 }}>This action cannot be undone.</p>
          <div style={{ marginTop: 12, padding: 12, background: "#fff2f0", borderRadius: 6, border: "1px solid #ffccc7" }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.email}</p>
          </div>
        </div>
      ),
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.delete(`${API_CONST.DELETE_TALENT_PROFILE}/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          await fetchTalentProfiles();
          message.success(`${record.name} has been deleted`);
        } catch (error) {
          console.error("Error deleting talent:", error);
          message.error("Failed to delete talent profile");
        }
      },
    });
  };

  const handleMarkActive = (record) => {
    const id = record.id || record.key;
    Modal.confirm({
      title: "Mark as Active",
      icon: <CheckCircleOutlined style={{ color: "#52c41a" }} />,
      content: (
        <div>
          <p>Are you sure you want to mark this talent as active?</p>
          <div style={{ marginTop: 12, padding: 12, background: "#f6ffed", borderRadius: 6, border: "1px solid #b7eb8f" }}>
            <p style={{ margin: 0, fontWeight: 500 }}>{record.name}</p>
            <p style={{ margin: "4px 0 0", color: "#666", fontSize: 13 }}>{record.organization}</p>
          </div>
        </div>
      ),
      okText: "Mark Active",
      okButtonProps: { style: { background: "#52c41a", borderColor: "#52c41a" } },
      cancelText: "Cancel",
      onOk: async () => {
        const success = await updateTalentStatus(id, "Active");
        if (success) {
          message.success(`${record.name} has been marked as active`);
        }
      },
    });
  };

  const handleBulkStatusChange = (newStatus) => {
    const selectedTalents = usersData.filter(user => selectedRowKeys.includes(user.id));
    const talentIds = selectedTalents.map(t => t.id);
    const statusColor = newStatus === "Active" ? "#52c41a" : "#faad14";
    const bgColor = newStatus === "Active" ? "#f6ffed" : "#fff7e6";
    const borderColor = newStatus === "Active" ? "#b7eb8f" : "#ffe58f";
    
    Modal.confirm({
      title: `Mark ${selectedRowKeys.length} Talent(s) as ${newStatus}`,
      icon: newStatus === "Active" ? 
        <CheckCircleOutlined style={{ color: statusColor }} /> : 
        <StopOutlined style={{ color: statusColor }} />,
      content: (
        <div>
          <p>Are you sure you want to mark the following talents as {newStatus.toLowerCase()}?</p>
          <div style={{ marginTop: 12, padding: 12, background: bgColor, borderRadius: 6, border: `1px solid ${borderColor}`, maxHeight: 150, overflow: "auto" }}>
            {selectedTalents.map(talent => (
              <p key={talent.id} style={{ margin: "4px 0", fontSize: 13 }}>{talent.name}</p>
            ))}
          </div>
        </div>
      ),
      okText: `Mark ${newStatus}`,
      okButtonProps: { style: { background: statusColor, borderColor: statusColor } },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.post(API_CONST.BULK_TALENT_STATUS_CHANGE, { talentIds, status: newStatus }, {
            headers: { Authorization: `Bearer ${token}` },
          });
          await fetchTalentProfiles();
          setSelectedRowKeys([]);
          message.success(`${selectedRowKeys.length} talent(s) marked as ${newStatus.toLowerCase()}`);
        } catch (error) {
          console.error("Error updating bulk status:", error);
          message.error("Failed to update status");
        }
      },
    });
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };

  const getActionMenuItems = (record) => [
    {
      key: "view-docs",
      label: "View Documents",
      icon: <FileTextOutlined />,
      onClick: () => handleViewDocuments(record),
    },
    {
      key: "edit",
      label: "Edit",
      icon: <EditOutlined />,
      onClick: () => handleEditTalent(record),
    },
    { type: "divider" },
    ...(record.status === "Active" ? [{
      key: "inactive",
      label: "Mark Inactive",
      icon: <StopOutlined />,
      onClick: () => handleMarkInactive(record),
    }] : [{
      key: "active",
      label: "Mark Active",
      icon: <CheckCircleOutlined />,
      onClick: () => handleMarkActive(record),
    }]),
    {
      key: "delete",
      label: "Delete",
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDeleteTalent(record),
    },
  ];

  const columns = [
    { 
      title: "Name", 
      dataIndex: "name", 
      key: "name",
      width: 160,
      render: (text, record) => (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <a 
            onClick={() => navigate(`/admin/talent-profiles/details/${record.id || record.key}`)}
            style={{ color: "#014c75", fontWeight: 500, cursor: "pointer" }}
          >
            {text}
          </a>
          {record.idVerificationStatus === "Verified" && (
            <span style={{ fontSize: 11, color: "#52c41a", display: "flex", alignItems: "center", gap: 3 }}>
              <SafetyCertificateOutlined /> ID Verified
            </span>
          )}
          {record.idVerificationStatus === "Failed" && (
            <span style={{ fontSize: 11, color: "#ff4d4f", display: "flex", alignItems: "center", gap: 3 }}>
              <SafetyCertificateOutlined /> ID Failed
            </span>
          )}
        </div>
      ),
    },
    { title: "Email Id", dataIndex: "email", key: "email", width: 180, ellipsis: true },
    { title: "Contact No", dataIndex: "contact", key: "contact", width: 150 },
    { 
      title: "Organization", 
      dataIndex: "organization", 
      key: "organization", 
      width: 140, 
      ellipsis: true,
      render: (org) => org ? (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: "/admin/talent-profiles", fromLabel: "Back to Talent Profiles" }
          })}
        >
          {org}
        </a>
      ) : "-",
    },
    { title: "Role", dataIndex: "role", key: "role", width: 150, ellipsis: true },
    { title: "Experience", dataIndex: "experience", key: "experience", width: 120 },
    { 
      title: "Partner Rate", 
      dataIndex: "partnerRate", 
      key: "partnerRate",
      width: 115,
      render: (value) => value ? (
        <span style={{ fontWeight: 600, color: "#014c75" }}>{formatCurrency(value)}</span>
      ) : "-",
    },
    { 
      title: "Settled Cost", 
      key: "settledCost",
      width: 115,
      render: (_, record) => record.settledCost ? (
        <span style={{ fontWeight: 600, color: "#52c41a" }}>{formatCurrency(record.settledCost)}</span>
      ) : "-",
    },
    { 
      title: "Client Rate", 
      key: "clientRate",
      width: 110,
      render: (_, record) => record.clientRate ? (
        <span style={{ fontWeight: 600, color: "#1890ff" }}>{formatCurrency(record.clientRate)}</span>
      ) : "-",
    },
    { 
      title: "Jobs Applied", 
      dataIndex: "jobsApplied", 
      key: "jobsApplied", 
      width: 110, 
      align: "center",
      render: (value) => (
        <span style={{ background: "#e6f7ff", padding: "4px 10px", borderRadius: 4, fontWeight: 500, color: "#1890ff" }}>
          {value || 0}
        </span>
      ),
    },
    { 
      title: "Past Hired", 
      dataIndex: "pastHired", 
      key: "pastHired", 
      width: 100, 
      align: "center",
      render: (value) => (
        <span style={{ background: "#f6ffed", padding: "4px 10px", borderRadius: 4, fontWeight: 500, color: "#52c41a" }}>
          {value || 0}
        </span>
      ),
    },
    { 
      title: "Rejected", 
      dataIndex: "rejected", 
      key: "rejected", 
      width: 100, 
      align: "center",
      render: (value) => (
        <span style={{ background: "#fff2f0", padding: "4px 10px", borderRadius: 4, fontWeight: 500, color: "#ff4d4f" }}>
          {value || 0}
        </span>
      ),
    },
    { 
      title: "Rejection Rate", 
      key: "rejectionRate", 
      width: 120,
      align: "center",
      render: (_, record) => {
        if (!record.jobsApplied || record.jobsApplied === 0) return "-";
        const rate = ((record.rejected || 0) / record.jobsApplied * 100).toFixed(1);
        return (
          <span style={{ background: "#fff7e6", padding: "4px 10px", borderRadius: 4, fontWeight: 500, color: "#faad14" }}>
            {rate}%
          </span>
        );
      },
    },
    { title: "Created on", dataIndex: "createdOn", key: "createdOn", width: 100 },
    {
      title: "Action",
      key: "action",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={["click"]}
        >
          <MoreOutlined style={{ cursor: "pointer", fontSize: 18 }} />
        </Dropdown>
      ),
    },
  ];

  return (
    <UserManagementWrapper>
      <PageHeader><h1>Talent Profiles</h1><p>Browse and manage talent profiles</p></PageHeader>
      
      <MetricsContainer>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<UserAddOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#e6f7ff" }}
            />
            <div className="metric-info">
              <h3>{totalCount}</h3>
              <p>Total Profiles</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<FileTextOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#fff7e6" }}
            />
            <div className="metric-info">
              <h3>{activeCount}</h3>
              <p>Active Profiles</p>
            </div>
          </div>
        </Card>
        <Card className="metric-card">
          <div className="metric-content">
            <Avatar
              size={64}
              icon={<TrophyOutlined />}
              className="metric-icon"
              style={{ backgroundColor: "#f6ffed" }}
            />
            <div className="metric-info">
              <h3>{inactiveCount}</h3>
              <p>Inactive Profiles</p>
            </div>
          </div>
        </Card>
      </MetricsContainer>

      <TabsContainer>
        {["Active", "Inactive"].map((tab) => (
          <Button
            key={tab}
            className={activeTab === tab ? "tab-button active" : "tab-button"}
            onClick={() => {
              setActiveTab(tab);
              setSelectedRowKeys([]);
            }}
          >
            {tab}
          </Button>
        ))}
      </TabsContainer>

        <Flex align="center" justify="space-between" style={{ marginBottom: 16 }} wrap="wrap" gap={12}>
          <Flex align="center" gap={12} wrap="wrap">
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by Name"
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <Select
              placeholder="Location"
              allowClear
              style={{ width: 140 }}
              value={locationFilter}
              onChange={setLocationFilter}
              options={locationOptions.map(loc => ({ label: loc, value: loc }))}
            />
            <Select
              placeholder="Role"
              allowClear
              style={{ width: 160 }}
              value={roleFilter}
              onChange={setRoleFilter}
              options={roleOptions.map(role => ({ label: role, value: role }))}
            />
            <Select
              placeholder="Experience"
              allowClear
              style={{ width: 130 }}
              value={experienceFilter}
              onChange={setExperienceFilter}
              options={experienceOptions.map(exp => ({ label: exp, value: exp }))}
            />
            <Select
              placeholder="Organization"
              allowClear
              style={{ width: 180 }}
              value={organizationFilter}
              onChange={setOrganizationFilter}
              options={organizationOptions.map(org => ({ label: org, value: org }))}
            />
            {selectedRowKeys.length > 0 && (
              <Button
                type="primary"
                icon={activeTab === "Active" ? <StopOutlined /> : <CheckCircleOutlined />}
                onClick={() => handleBulkStatusChange(activeTab === "Active" ? "Inactive" : "Active")}
                style={{ 
                  backgroundColor: activeTab === "Active" ? "#faad14" : "#52c41a",
                  borderColor: activeTab === "Active" ? "#faad14" : "#52c41a",
                  height: "32px",
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              >
                Mark {activeTab === "Active" ? "Inactive" : "Active"} ({selectedRowKeys.length})
              </Button>
            )}
          </Flex>
          <Link to="/admin/talent-profiles/add-new-profile">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              style={{ 
                backgroundColor: "#00d9a9",
                borderColor: "#00d9a9",
                height: "36px",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              Add New Profile
            </Button>
          </Link>
        </Flex>
        <Table
          rowKey="id"
          rowSelection={rowSelection}
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          pagination={{ pageSize: 5, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
          scroll={{ x: 1800 }}
          style={{ fontSize: 13 }}
        />
    </UserManagementWrapper>
  );
};

export default TalentProfiles;
